import { ShimmerLoader, StandardAlert } from '@brandfolder/react';
import React, { useEffect, FunctionComponent } from 'react';

import { useFetch } from '@api/ApiHelper';
import { PlanGetResponse } from '@api/v4/resources/plan';

import './styles/plan-limits.scss';

interface UserLimitProps {
  count: number;
  invitationCount: number;
  invitationsLabel: string;
  label: string;
  limit: number;
  loading: boolean;
}

const UserLimit: FunctionComponent<UserLimitProps> = ({
  count,
  invitationCount,
  invitationsLabel,
  label,
  limit,
  loading
}) => (
  <div className="user-limit">
    {loading ? <ShimmerLoader numLines={3} /> : (
      <>
        <div className="user-limit__count user-limit__count--users">
          <div className="user-limit__count--users-wrapper">
            <span className="user-limit__count--big">{count}</span>
            {` / ${limit === -1 ? bfTranslate('unlimited') : limit}`}
          </div>
          <div>
            {label}
          </div>
        </div>
        <div className="user-limit__count user-limit__count--availability">
          {limit === -1
            ? bfTranslate('unlimited spots available')
            : `${limit - count} ${bfTranslate('spots available of')} ${limit}`}
        </div>
        <div className="user-limit__count user-limit__count--invitations">
          <div>
            <span className="user-limit__count--big">{invitationCount}</span>
          </div>
          <div className="user-limit__count--invitations-label">
            {invitationsLabel}
          </div>
        </div>
      </>
    )}
  </div>
);

interface PlanLimitsProps {
  reload: boolean;
  setReload: SetStateDispatch<boolean>;
}

export const PlanLimits: FunctionComponent<PlanLimitsProps> = ({ reload, setReload }) => {
  const planLimitsFetch = useFetch<PlanGetResponse>({
    url: `/api/v4/${BFG.resource.type}s/${BFG.resource.key}/plan`,
    fields: ['admin_invites', 'admins', 'collaborator_invites', 'collaborators']
  });

  useEffect(() => {
    if (reload) {
      setReload(false);
      planLimitsFetch.fetch();
    }
  }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

  let planLimitsBody;

  if (planLimitsFetch.error) {
    planLimitsBody = (
      <StandardAlert
        className="plan-limits__warning-message"
        showHeading={false}
      >
        {bfTranslate("We were unable to retrieve information regarding your account's plan limits. Try again.")}
      </StandardAlert>
    );
  } else if (planLimitsFetch.loading || planLimitsFetch.response) {
    planLimitsBody = (
      <div className="plan-limits__user-limits-wrapper">
        <UserLimit
          count={planLimitsFetch.response?.data.attributes.admins}
          invitationCount={planLimitsFetch.response?.data.attributes.admin_invites}
          invitationsLabel={bfTranslate('pending invites')}
          label={bfTranslate('Admins and Owners')}
          limit={planLimitsFetch.response?.data.attributes.limits.admins}
          loading={planLimitsFetch.loading}
        />
        <UserLimit
          count={planLimitsFetch.response?.data.attributes.collaborators}
          invitationCount={planLimitsFetch.response?.data.attributes.collaborator_invites}
          invitationsLabel={bfTranslate('pending invites')}
          label={bfTranslate('Collaborators')}
          limit={planLimitsFetch.response?.data.attributes.limits.collaborators}
          loading={planLimitsFetch.loading}
        />
      </div>
    );
  }

  return (
    <div className="plan-limits">
      <h3>{bfTranslate('Plan Limits')}</h3>
      {planLimitsBody}
    </div>
  );
};